import { AssignmentCategory, AssignmentStatus, MasteryPacing, ResourceType } from "@/enums";
import {
  PlannerCurriculum,
  PlannerCurriculumLesson,
} from "@/features/planner/PlannerTypes";
import {
  AssignmentManifest,
  parseMasteryPacingToClassName,
  SkillMasteryRecord,
  Student,
} from "@/models";
import {
  calcDisplayAssignmentCategory
} from "./aggregateSkillGroups";
import { StudentPlaylistEntry, StudentPlaylistObjective } from "@/models/StudentPlaylist";
import { Skill } from "@/services/assignment/assignmentTypesAPI";
import { getTimeAssignedByGrade, getMostCommonGrade } from "@/utilities";

const filterManifestBySkillIds = (manifest: AssignmentManifest[], skillIds: string[]) => {
  return manifest.filter((m) => m.skills?.some((s) => skillIds.includes(s.skillId!)));
}

export const aggregateLessonsToStudentPlaylistEntries = (
  curriculum: PlannerCurriculum,
  students: Student[],
  userId: string,
  classroomId: string,
  masteryRecords: { [key: string]: SkillMasteryRecord[] }
) => {
  const result = [] as StudentPlaylistEntry[];
  
  // Calculate class predominant grade once
  const classGrade = (getMostCommonGrade(students.map(s => ({ grade: s.grade?.toString() ?? '1' }))) ?? 1).toString();

  students.forEach((student) => {
    const entry = {
      student,
      rolloverSkills: 0,
      prerequisiteSkills: 0,
      coreSkills: 0,
      teacherAssignedSkills: 0,
      assignedTimeMinutes: 0,
      objectives: [],
    } as StudentPlaylistEntry;

    curriculum.tracks.forEach(track => {
      track.units.forEach(unit => {
        unit.lessons.forEach(lesson => {
          const sa = lesson.studentAssignments.find(
            sa => student.id === sa.entityId
          );
          const assignment = sa
            ? structuredClone(sa)
            : structuredClone(lesson.mainAssignment);
          // combine the manifests of the main assignment and the student assignment
          // similar logic findAssignedSkills method
          assignment.manifest = [
            ...structuredClone(lesson.mainAssignment).manifest,
            ...structuredClone(sa || { manifest: [] }).manifest,
          ];

          assignment.manifest.forEach(m => {
            if (m.skills && m.skills.length) {
              const focusSkill = lesson.skills.find(
                fs => fs.skillId === m.skills![0].skillId
              );
              const skill = focusSkill ? focusSkill : m.skills[0];
              const assignmentCategory = calcDisplayAssignmentCategory(
                assignment.assignmentCategory ?? []
              );
              const skillAssignedStatus =
                m.status ?? assignment.status ?? AssignmentStatus.ASSIGNED;

              const foundObjective = entry.objectives.find(
                obj =>
                  obj.assignment.assignmentId !== "-1" &&
                  obj.assignment.assignmentId === assignment.assignmentId &&
                  obj.skill.skillId === skill.skillId &&
                  obj.assignmentCategory === assignmentCategory
              );
              let timeAssigned = getTimeAssignedByStudent(lesson, skill, student, masteryRecords, false, classGrade);
              // update time assigned on existing if find in objectives
              if (foundObjective) {
                foundObjective.timeAssigned = timeAssigned;
                // modify the assigned skill status to reflect multiple assigned stories and their status
                //    any story in progress sets result to in progress
                //    any story in assigned while all others are done set result to in progress
                //    all stories in assigned set result to assigned
                //    all stories in done set result to done
                if (
                  foundObjective.skillAssignedStatus !== skillAssignedStatus &&
                  foundObjective.skillAssignedStatus !==
                    AssignmentStatus.IN_PROGRESS
                ) {
                  if (skillAssignedStatus === AssignmentStatus.IN_PROGRESS) {
                    foundObjective.skillAssignedStatus =
                      AssignmentStatus.IN_PROGRESS;
                  } else {
                    switch (foundObjective.skillAssignedStatus) {
                      case AssignmentStatus.COMPLETE:
                      case AssignmentStatus.DONE:
                        if (
                          skillAssignedStatus !== AssignmentStatus.DONE &&
                          skillAssignedStatus !== AssignmentStatus.COMPLETE
                        ) {
                          foundObjective.skillAssignedStatus =
                            AssignmentStatus.IN_PROGRESS;
                        }
                        break;
                      default:
                        break;
                    }
                  }
                }
              } else {
                // else create a new objective and add it
                let masteryPacing = `${MasteryPacing.NO_DATA}`;
                const lessonMasteryRecords =
                  masteryRecords[lesson.lessonPlanId];
                if (lessonMasteryRecords) {
                  const masteryRecord = lessonMasteryRecords.find(
                    mr => mr.studentId === student.id
                  );
                  const foundRecord = masteryRecord?.records.find(
                    record => record.skillId === skill.skillId
                  );
                  if (foundRecord) {
                    masteryPacing = foundRecord.pacing;
                    timeAssigned = getTimeAssignedByStudent(lesson, skill, student, masteryRecords, false, classGrade);
                  }
                }

                const objective = {
                  assignment: sa ? sa : lesson.mainAssignment,
                  lesson: {
                    ...lesson,
                    students: [student],
                    userId,
                    classroomId,
                    curriculumName: curriculum.name,
                    trackName: track.name,
                    unitName: unit.name,
                    masteryRecords: lessonMasteryRecords
                      ? lessonMasteryRecords
                      : [],
                    // Keep the full mainAssignment manifest to prevent cross-skill assignment issues
                    mainAssignment: {
                      ...lesson.mainAssignment,
                      // Don't filter the manifest here - the assignment modal needs the full context
                      // to properly handle cross-skill assignments when deleting/adding resources
                    },
                  },
                  skill,
                  skillAssignedStatus,
                  assignmentCategory,
                  masteryPacing: parseMasteryPacingToClassName(masteryPacing),
                  timeAssigned,
                  // used to have `Objective ${entry.objectives.length + 1}: ` before skill display on objective
                  objective: `${
                    skill.displayName
                      ? skill.displayName
                      : skill.skillName
                        ? skill.skillName
                        : skill.skillId
                  }`,
                } as StudentPlaylistObjective;
                entry.objectives.push(objective);
                // update skill count only on new entries (different than total stories assigned)
                switch (assignmentCategory) {
                  case AssignmentCategory.CORE:
                    entry.coreSkills += 1;
                    break;
                  case AssignmentCategory.PREREQUISITE:
                    entry.prerequisiteSkills += 1;
                    break;
                  case AssignmentCategory.ROLLOVER:
                    entry.rolloverSkills += 1;
                    break;
                  case AssignmentCategory.TEACHER_ASSIGNED:
                    entry.teacherAssignedSkills += 1;
                    break;
                  case AssignmentCategory.AI: // TODO is this prereq or teacher assigned?
                    break;
                  default:
                    break;
                }
              }
            }
          });
        });
      });
    });
    // update entry times
    entry.assignedTimeMinutes = entry.objectives.reduce((acc, obj) => acc + obj.timeAssigned, 0);
    
    // Create a map to count occurrences of each skillId
    const skillIdCounts = new Map<string, number>();
    entry.objectives.forEach(obj => {
      const skillId = obj.skill.skillId;
      if (skillId) {
        skillIdCounts.set(skillId, (skillIdCounts.get(skillId) || 0) + 1);
      }
    });
    
    // Set isCommonObjective based on whether the skillId appears more than once
    entry.objectives.forEach(obj => {
      const skillId = obj.skill.skillId;
      obj.isCommonObjective = skillId ? (skillIdCounts.get(skillId) || 0) > 1 : false;
    });
    result.push(entry);
  });

  return result;
};

export const getTimeAssignedByStudent = (lesson: PlannerCurriculumLesson, skill: Skill, student: Student, masteryRecords: { [key: string]: SkillMasteryRecord[] }, includeTeacherResources: boolean = false, classGrade?: string) => {
  const lessonMasteryRecords = masteryRecords[lesson.lessonPlanId];
  if (lessonMasteryRecords) {
    const masteryRecord = lessonMasteryRecords.find(mr => mr.studentId === student.id);
    const foundRecord = masteryRecord?.records.find(record => record.skillId === skill.skillId);
    const masteryGroup = foundRecord ? parseMasteryPacingToClassName(foundRecord.pacing) : parseMasteryPacingToClassName(MasteryPacing.NO_DATA);
    
    if (includeTeacherResources) {
      // Use the original calculation that includes teacher resources
      return lesson.timeAssigned[skill.skillId!]?.[masteryGroup]?.[student.id] ?? 0;
    } else {
      // Use the calculation that excludes teacher resources
      return getTimeAssignedExcludingTeacherResources(lesson, skill.skillId!, masteryGroup, student, classGrade);
    }
  }
  return 0;
}

const getTimeAssignedExcludingTeacherResources = (lesson: PlannerCurriculumLesson, skillId: string, masteryGroup: string, student: Student, classGrade?: string) => {
  // Get the original time assigned (which includes teacher resources)
  const originalTime = lesson.timeAssigned[skillId]?.[masteryGroup]?.[student.id] ?? 0;
  
  // Calculate teacher resource time to subtract
  let teacherResourceTime = 0;
  
  // Check lesson resources for teacher resources assigned to this skill/student
  const skillResources = lesson.resources.find(r => r.skillId === skillId);
  if (skillResources) {
    skillResources.resources.forEach(resource => {
      if (resource.resourceType === ResourceType.teacherResource) {
        // Check if this teacher resource is assigned to the student
        const studentAssignment = lesson.studentAssignments.find(sa => sa.entityId === student.id);
        const assignment = studentAssignment || lesson.mainAssignment;
        
        // Check if this resource is in the assignment's resources array
        if (assignment.resources?.includes(resource.resourceUniqueId)) {
          // Use class predominant grade if provided, otherwise fall back to individual grade
          if (classGrade) {
            teacherResourceTime += getTimeAssignedByGrade(ResourceType.teacherResource, [{ grade: classGrade }]);
          } else {
            // Fallback to individual student grade for backward compatibility
            const studentGrade = student.grade?.toString() ?? '1';
            teacherResourceTime += getTimeAssignedByGrade(ResourceType.teacherResource, [{ grade: studentGrade }]);
          }
        }
      }
    });
  }
  
  // Return original time minus teacher resource time
  return Math.max(0, originalTime - teacherResourceTime);
}